//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import _ from 'lodash';
import Papa from 'papaparse';
import { useListableProperties } from '@/api/legacy/useListableProperties';
import { isLink, naturalSort, reverseNaturalSort, saveFile } from '@/helpers/util';
import Convert from '../../../mixins/convert'; // tmw_used
import DpWidgetLoader from '../loader/WidgetLoader.vue';
import GridHeader from './GridHeader.vue';
import GridRows from './GridRows.vue';

export default {
  name: 'DpGrid',
  components: {
    GridHeader,
    GridRows,
    DpWidgetLoader,
  },
  mixins: [Convert],
  props: {
    // Device data
    data: { type: Array, default: () => [] },
    // Whether to show/hide select column
    selectable: { type: Boolean, default: false },
    selected: { type: Array, default: () => [] },
    // Options for columns, will add $ts default if it does not exist
    columnOptions: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    menuEnabled: { type: Boolean, default: false },
    // Properties that will be hidden from the property selector
    hiddenProperties: { type: Array, default: () => [] },
    autoSelectFirst: { type: Boolean, default: false },
    kpi: { type: Object, default: null },
    historyMode: { type: Boolean, default: false },
    // generic mode
    generic: { type: Boolean, default: false },
    explicitKey: { type: String, default: null }, // if not using $id; stops virtualisation breaking
  },
  setup() {
    const { listableProperties } = useListableProperties();
    return { listableProperties };
  },
  data() {
    return {
      loaded: false,
      columns: [],
      name: null,
      resizeSensor: null,
      selection: [],
      internalColumnOptions: {},
      hasSelectedFirst: false,
    };
  },
  computed: {
    // Data that has been sorted and filtered as required by columns
    computedDeviceData() {
      if (!this.columns) {
        return this.data;
      }

      let sort = { key: '$id', order: 'asc', isTs: false }; // default to $id sorting
      const flatColumns = {};
      this.columns.forEach((col) => {
        flatColumns[col.name] = col;
        if (col.sort) {
          sort = { key: col.name, order: col.sort, isTs: col.isTs };
        }
      });

      if (this.internalColumnOptions.$kpi && this.internalColumnOptions.$kpi.sort) {
        sort = { key: '$kpiRaw', order: this.internalColumnOptions.$kpi.sort };
      }

      let returnedData = this.data.map((d) => {
        const dataItem = d;
        const column = flatColumns[sort.key];
        let val;
        if (column) {
          val = d[column.name];
        } else {
          val = d[sort.key];
        }
        dataItem.$sort = val;
        return dataItem;
      });

      // Date sorting
      const sortFormat = sort.isTs ? 'moment' : null;
      if (sort.order === 'asc') {
        returnedData = naturalSort(returnedData, '$sort', sortFormat);
      } else {
        returnedData = reverseNaturalSort(returnedData, '$sort', sortFormat);
      }

      return returnedData;
    },
    // Fetch the settings object
    settings() {
      return {
        selectEnabled: this.selectable,
        menuEnabled: this.menuEnabled,
        hiddenProperties: this.hiddenProperties,
      };
    },
  },
  watch: {
    data() {
      this.updateColumnDefinitions();
      if (!this.hasSelectedFirst && this.autoSelectFirst && this.selection.length === 0
        && this.computedDeviceData.length && this.computedDeviceData[0]) {
        this.hasSelectedFirst = true;
        this.handleSelectionChange([this.computedDeviceData[0]]);
      }
    },
    columnOptions() {
      this.cloneColumnOptions();
      // hide this update behind the generic functionality just in case it regresses
      if (this.generic) this.updateColumnDefinitions();
    },
    selected(newSelection) {
      this.selection = newSelection;
    },
  },
  // Initial load
  mounted() {
    this.cloneColumnOptions();
    this.updateGrid();
    this.loaded = true;
    this.selection = this.selected;
  },
  methods: {
    updateColumnDefinitions(passedData, saveState) {
      if (passedData) {
        this.columns = passedData;
        return;
      }

      if (!this.data) {
        this.columns = [];
      }

      const uniqueColumns = [];
      const foundArray = [];

      // Add any columns that don't contain data in the current set
      Object.keys(this.internalColumnOptions).forEach((k) => {
        const opt = this.internalColumnOptions[k];
        if (foundArray.indexOf(k) === -1 && (opt.visible === undefined || opt.visible)) {
          foundArray.push(k);
          const prop = this.convertPropertyNameToProperty(k);
          if (prop) {
            uniqueColumns.push(prop);
          } else if (this.generic) {
            uniqueColumns.push({
              name: k,
              alias: k,
              format: (v) => v,
            });
          }
        }
      });

      // Get the fresh column object which takes into account the column settings
      this.columns = uniqueColumns
        .filter((prop) => prop !== null && prop !== undefined)
        .map((prop) => this.getColumnObject(prop))
        .filter((col) => col.visible)
        .sort((a, b) => a.order - b.order);

      if (saveState) {
        this.$emit('change-grid-state', {
          ...this.internalColumnOptions,
        });
      }
    },
    // Create a column object which gets used to define the behaviour of a column
    getColumnObject(prop = {}) {
      const {
        name,
        alias,
        isTs,
        format,
      } = prop;
      const opts = this.internalColumnOptions[name];

      return {
        name,
        format,
        isTs,
        order: opts.order || 0,
        visible: (opts && opts.visible !== undefined) ? opts.visible : true,
        sort: (opts && opts.sort !== undefined) ? opts.sort : null,
        width: (opts && opts.width)
          ? opts.width
          : null,
        isLink: (content) => isLink(content),
        // Name displayed in header bar
        getDisplayName: () => {
          if (opts && opts.label) {
            return opts.label;
          }
          return alias;
        },
        // Formatted row cell content
        getContent: (content) => {
          if (prop.isTs) {
            return format(content);
          }
          return content;
        },
      };
    },
    // Update the grid information (grid size etc.)
    updateGrid() {
      this.updateColumnDefinitions();
    },
    handleSelectionChange(selection) {
      if (!this.selectable) {
        return;
      }
      this.selection = selection;
      this.$emit('selection-changed', selection);
    },
    updateSort(column) {
      if (!this.internalColumnOptions[column.name]) {
        this.internalColumnOptions[column.name] = {
          sort: 'asc',
        };
      } else if (this.internalColumnOptions[column.name].sort === 'asc') {
        this.internalColumnOptions[column.name].sort = 'desc';
      } else {
        this.internalColumnOptions[column.name].sort = 'asc';
      }

      // Remove sorting from other columns
      Object.keys(this.internalColumnOptions).forEach((k) => {
        if (k !== column.name) {
          this.internalColumnOptions[k].sort = null;
        }
      });

      this.updateColumnDefinitions(null, true);
    },
    resizeColumn(e) {
      const { column, width } = e;

      if (!this.internalColumnOptions[column.name]) {
        this.internalColumnOptions[column.name] = {};
      }
      this.internalColumnOptions[column.name].width = width;
      this.updateColumnDefinitions(null, true);
    },
    addColumn(property) {
      const prop = property.name;
      let nextOrder = 0;

      Object.keys(this.internalColumnOptions).forEach((k) => {
        if (this.internalColumnOptions[k].order >= nextOrder) {
          nextOrder = this.internalColumnOptions[k].order + 1;
        }
      });

      this.internalColumnOptions[prop] = { visible: true, order: nextOrder };
    },
    toggleColumn(property) {
      const prop = property.name;
      if (!this.internalColumnOptions[prop]) {
        this.addColumn(property);
      } else {
        this.internalColumnOptions[prop].visible = !this.internalColumnOptions[prop].visible;
      }
      this.updateColumnDefinitions(null, true);
    },
    moveColumn({ from, to }) {
      const fromLeft = from.order < to.order;

      this.internalColumnOptions[from.name].order = to.order;

      Object.keys(this.internalColumnOptions).forEach((k) => {
        const opt = this.internalColumnOptions[k];

        // Don't move the already moved column
        if (k === from.name) {
          return;
        }

        // Ignore options outside of the ones that need to be shifted
        if (fromLeft) { // from left
          if ((opt.order < from.order) || (opt.order > to.order)) {
            return;
          }
        } else if (!fromLeft) { // from right
          if ((opt.order > from.order) || (opt.order < to.order)) {
            return;
          }
        }

        if (fromLeft) {
          this.internalColumnOptions[k].order -= 1;
        } else {
          this.internalColumnOptions[k].order += 1;
        }
      });

      this.updateColumnDefinitions(null, true);
    },
    cloneColumnOptions() {
      this.internalColumnOptions = _.cloneDeep(this.columnOptions);

      if (!this.internalColumnOptions) {
        this.internalColumnOptions = {};
      }

      if (this.generic) return;

      // Default to base grid settings
      if (Object.keys(this.internalColumnOptions).length === 0) {
        this.internalColumnOptions = {
          $id: { visible: true, order: 0 },
          $ts: { visible: false, order: 1 },
        };
      }

      // Add $id column as default
      if (!this.internalColumnOptions.$id) {
        this.internalColumnOptions.$id = {};
      }

      // Add the timestamp column as a default formatting if it has not been passed in
      if (!this.internalColumnOptions.$ts) {
        this.internalColumnOptions.$ts = {
          label: 'Last seen',
          visible: false,
        };
      }

      // If we find no sort, then we default to "$id" sorting
      let foundSearch = false;
      Object.keys(this.internalColumnOptions).forEach((k) => {
        if (this.internalColumnOptions[k].sort) {
          foundSearch = true;
        }
      });
      if (!foundSearch) {
        this.internalColumnOptions.$id.sort = 'asc';
      }
    },
    exportAsCsv() {
      const visibleColumns = this.columns.filter((c) => c.visible || c.visible === undefined);
      const columns = (this.kpi ? [{ name: this.kpi.name }] : [])
        .concat(naturalSort(visibleColumns, 'order'));

      const exportData = this.computedDeviceData.map((datum) => {
        const returnedDatum = {};

        columns.forEach((col) => {
          const d = datum[col.name];
          const prop = this.convertPropertyNameToProperty(col.name);

          if (this.kpi && col.name === this.kpi.name) {
            returnedDatum[col.name] = (datum.$kpi && datum.$kpi.value) || null;
          } else if (prop && prop.isTs) {
            let val = prop.format(d);
            if (val) {
              val = val.replace(',', '');
            }
            returnedDatum[col.getDisplayName()] = val || '';
          } else if (prop) {
            returnedDatum[col.getDisplayName()] = d !== undefined ? d : '';
          }
        });

        return returnedDatum;
      });

      const csv = Papa.unparse(exportData);
      const blob = new Blob([
        new Uint8Array([0xEF, 0xBB, 0xBF]), // set UTF-8 BOM
        csv,
      ], { type: 'text/csv;charset=utf-8' });

      saveFile(blob, 'tableData.csv');
    },
  },
};
